import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Categoria, CategoriaFormData } from '@/types/categoria';
import { useToast } from '@/hooks/use-toast';
import { useClientFilter } from './useClientFilter';

export const useCategorias = (options?: { skipGlobalFilter?: boolean }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentClientId, showAllClients, clientLoading } = useClientFilter();

  const skipGlobal = options?.skipGlobalFilter ?? false;

  const {
    data: categorias = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['categorias', skipGlobal ? 'all' : currentClientId, skipGlobal ? true : showAllClients],
    queryFn: async () => {
      let query = supabase
        .from('categorias_servico')
        .select(`
          *,
          categoria_pai:categorias_servico!categoria_pai_id(nome),
          cliente:clients(name),
          grupo:groups(name),
          sla:slas(nome),
          usuario_responsavel:users!usuario_responsavel_id(name)
        `);

      // Aplicar filtro de cliente se necessário (pular se skipGlobalFilter)
      if (!skipGlobal && currentClientId && !showAllClients) {
        query = query.eq('client_id', currentClientId);
      }

      const { data, error } = await query.order('ordem_exibicao', { ascending: true });

      if (error) {
        console.error('Error fetching categorias:', error);
        throw error;
      }

      
      // Transform the data to ensure proper typing and handle any join errors
      const transformedData = (data || []).map(item => ({
        ...item,
        categoria_pai: item.categoria_pai || null,
        cliente: item.cliente || null,
        grupo: item.grupo || null,
        sla: item.sla || null,
        usuario_responsavel: item.usuario_responsavel || null,
      }));

      return transformedData as Categoria[];
    },
    enabled: !clientLoading,
  });

  const createCategoria = useMutation({
    mutationFn: async (categoriaData: CategoriaFormData) => {
      const { data, error } = await supabase
        .from('categorias_servico')
        .insert([categoriaData])
        .select()
        .single();

      if (error) {
        console.error('Error creating categoria:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      toast({
        title: "Sucesso",
        description: "Categoria criada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error creating categoria:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar categoria. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const updateCategoria = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CategoriaFormData> }) => {
      const { data: updatedData, error } = await supabase
        .from('categorias_servico')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating categoria:', error);
        throw error;
      }

      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      toast({
        title: "Sucesso",
        description: "Categoria atualizada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error updating categoria:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar categoria. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const deleteCategoria = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('categorias_servico')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting categoria:', error);
        throw error;
      }

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      toast({
        title: "Sucesso",
        description: "Categoria removida com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Error deleting categoria:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover categoria. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    categorias,
    isLoading,
    error,
    createCategoria,
    updateCategoria,
    deleteCategoria,
  };
};
